import React, { useState, useEffect } from 'react';
import { X, Search, Link as LinkIcon, AlertCircle, CheckCircle, User, FileText } from 'lucide-react';
import { PatientLinkingService, UserWithLinkStatus, PatientWithLinkStatus } from '../../services/PatientLinkingService';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { matchesPatientSearch } from '../../utils/patientSearch';

interface PatientUserLinkModalProps {
  clientId: string;
  onClose: () => void;
  onSuccess: () => void;
  preSelecteduserId?: string;
  preSelectedPatientId?: string;
}

const PatientUserLinkModal: React.FC<PatientUserLinkModalProps> = ({
  clientId,
  onClose,
  onSuccess,
  preSelecteduserId,
  preSelectedPatientId
}) => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserWithLinkStatus[]>([]);
  const [patients, setPatients] = useState<PatientWithLinkStatus[]>([]);
  const [selecteduserId, setSelecteduserId] = useState<string>(preSelecteduserId || '');
  const [selectedPatientId, setSelectedPatientId] = useState<string>(preSelectedPatientId || '');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [potentialMatches, setPotentialMatches] = useState<PatientWithLinkStatus[]>([]);

  useEffect(() => {
    loadData();
  }, [clientId]);

  useEffect(() => {
    if (selecteduserId) {
      findMatches(selecteduserId);
    } else {
      setPotentialMatches([]);
    }
  }, [selecteduserId, clientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersData, patientsData] = await Promise.all([
        PatientLinkingService.getAllUsersWithLinkStatus(clientId),
        PatientLinkingService.getAllPatientsWithLinkStatus(clientId)
      ]);
      setUsers(usersData);
      setPatients(patientsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const findMatches = async (userId: string) => {
    try {
      const matches = await PatientLinkingService.findPotentialMatches(userId, clientId);
      setPotentialMatches(matches);
    } catch (error) {
      console.error('Erro ao buscar correspondências:', error);
      setPotentialMatches([]);
    }
  };

  const handleLink = async () => {
    if (!selecteduserId || !selectedPatientId) {
      toast.error('Selecione um usuário e um paciente');
      return;
    }
    if (!user?.userId) {
      toast.error('Usuário autenticado não encontrado');
      return;
    }

    const selectedUser = users.find(u => u.uid === selecteduserId);
    const selectedPatient = patients.find(p => p.id === selectedPatientId);

    if (selectedUser?.isLinked) {
      toast.error('Este usuário já está vinculado a outro paciente');
      return;
    }

    if (selectedPatient?.isLinked) {
      toast.error('Este paciente já está vinculado a outro usuário');
      return;
    }

    try {
      setSubmitting(true);
      await PatientLinkingService.linkPatientToUser(selecteduserId, selectedPatientId, user.userId, clientId);
      toast.success('Vinculação realizada com sucesso!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro ao vincular:', error);
      toast.error(error.message || 'Erro ao vincular paciente ao usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (user.isLinked && user.uid !== preSelecteduserId) return false;
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.displayName.toLowerCase().includes(searchLower)
    );
  });

  const filteredPatients = patients.filter(patient => {
    if (patient.isLinked && patient.id !== preSelectedPatientId) return false;
    return matchesPatientSearch(
      patientSearchTerm,
      `${patient.nome} ${patient.sobrenome}`,
      [patient.prontuario, patient.email],
    );
  });

  const selectedUser = users.find(u => u.uid === selecteduserId);
  const selectedPatient = patients.find(p => p.id === selectedPatientId);

  const canLink = selecteduserId && selectedPatientId && !selectedUser?.isLinked && !selectedPatient?.isLinked;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <LinkIcon className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Vincular Paciente a Usuário</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Selecionar Usuário
                    </h3>
                    <span className="text-sm text-blue-700">
                      {filteredUsers.length} disponíveis
                    </span>
                  </div>

                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Buscar por email ou nome..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>

                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {filteredUsers.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Nenhum usuário disponível
                      </p>
                    ) : (
                      filteredUsers.map(user => (
                        <div
                          key={user.uid}
                          onClick={() => !user.isLinked && setSelecteduserId(user.uid)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            selecteduserId === user.uid
                              ? 'border-blue-500 bg-blue-100'
                              : user.isLinked
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                              : 'border-gray-200 bg-white hover:border-blue-300'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 text-sm">{user.displayName || 'Sem nome'}</p>
                              <p className="text-xs text-gray-600">{user.email}</p>
                              {user.isLinked && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Já vinculado: {user.linkedPatientName}
                                </p>
                              )}
                            </div>
                            {selecteduserId === user.uid && (
                              <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-green-900 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Selecionar Paciente
                    </h3>
                    <span className="text-sm text-green-700">
                      {filteredPatients.length} disponíveis
                    </span>
                  </div>

                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Buscar por nome ou prontuário..."
                      value={patientSearchTerm}
                      onChange={(e) => setPatientSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {potentialMatches.length > 0 && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <p className="text-xs font-medium text-yellow-800 mb-2 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        Correspondências Sugeridas
                      </p>
                      <div className="space-y-1">
                        {potentialMatches.slice(0, 3).map(match => (
                          <button
                            key={match.id}
                            onClick={() => setSelectedPatientId(match.id)}
                            className="w-full text-left p-2 bg-white border border-yellow-300 rounded hover:bg-yellow-50 transition-colors text-xs"
                          >
                            <p className="font-medium text-gray-900">
                              {match.nome} {match.sobrenome}
                            </p>
                            <p className="text-gray-600">{match.prontuario}</p>
                            {match.email && <p className="text-gray-500">{match.email}</p>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {filteredPatients.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        Nenhum paciente disponível
                      </p>
                    ) : (
                      filteredPatients.map(patient => (
                        <div
                          key={patient.id}
                          onClick={() => !patient.isLinked && setSelectedPatientId(patient.id)}
                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedPatientId === patient.id
                              ? 'border-green-500 bg-green-100'
                              : patient.isLinked
                              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                              : 'border-gray-200 bg-white hover:border-green-300'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 text-sm">
                                {patient.nome} {patient.sobrenome}
                              </p>
                              <p className="text-xs text-gray-600">Prontuário: {patient.prontuario}</p>
                              {patient.email && (
                                <p className="text-xs text-gray-600">{patient.email}</p>
                              )}
                              {patient.isLinked && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Já vinculado: {patient.linkedUserEmail}
                                </p>
                              )}
                            </div>
                            {selectedPatientId === patient.id && (
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {selectedUser && selectedPatient && (
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <LinkIcon className="w-5 h-5" />
                    Preview da Vinculação
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                    <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
                      <p className="text-xs font-medium text-blue-800 mb-1">Usuário</p>
                      <p className="font-semibold text-gray-900 text-sm">{selectedUser.displayName}</p>
                      <p className="text-xs text-gray-600">{selectedUser.email}</p>
                    </div>
                    <div className="flex justify-center">
                      <LinkIcon className="w-8 h-8 text-gray-400" />
                    </div>
                    <div className="bg-green-100 border border-green-300 rounded-lg p-3">
                      <p className="text-xs font-medium text-green-800 mb-1">Paciente</p>
                      <p className="font-semibold text-gray-900 text-sm">
                        {selectedPatient.nome} {selectedPatient.sobrenome}
                      </p>
                      <p className="text-xs text-gray-600">Prontuário: {selectedPatient.prontuario}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleLink}
                  disabled={!canLink || submitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Vinculando...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4" />
                      Confirmar Vinculação
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientUserLinkModal;
